import React from "react";

import { InlineOption } from "@/Common/Components/Inputs/InlineOptionsControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FlowsheetConstants } from "@/Flowsheets/FlowsheetConstants";
import { ArterialBloodGasOptions } from "@/Flowsheets/Models/ArterialBloodGasOptions";
import { BooleanReviewedCareElement } from "@/Flowsheets/Models/CareElements/BooleanReviewedCareElement";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { CareElementType } from "@/Flowsheets/Models/CareElements/CareElementType";
import { DateOnlyReviewedCareElement } from "@/Flowsheets/Models/CareElements/DateOnlyReviewedCareElement";
import { DecimalReviewedCareElement } from "@/Flowsheets/Models/CareElements/DecimalReviewedCareElement";
import { IntegerReviewedCareElement } from "@/Flowsheets/Models/CareElements/IntegerReviewedCareElement";
import { MultiSelectReviewedCareElement } from "@/Flowsheets/Models/CareElements/MultiSelectReviewedCareElement";
import { TextReviewedCareElement } from "@/Flowsheets/Models/CareElements/TextReviewedCareElement";
import { ChronicObstructivePulmonaryDiseaseClassifications } from "@/Flowsheets/Models/ChronicObstructivePulmonaryDiseaseClassifications";
import CareComponentNotes from "./CareComponentParts/CareComponentNotes";
import CareElementDateOnlyDateReviewed from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateOnlyDateReviewed";
import CareElementDateReviewedCheckbox from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedCheckbox";
import CareElementDateReviewedNumber from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedNumber";
import { CareElementOptionsControl } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedOptionsControl";
import { CareElementDateReviewedRadioOptionsControl } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedRadioOptionsControl";
import { CareElementYesNoControl } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedYesNoControl";
import { ReviewedCareElementContainer } from "./CareComponentParts/DateReviewedCareElementComponents/ReviewedDateContainer";
import { ICareComponentProps } from "./Types";

import styles from "./ChronicObstructivePulmonaryDiseaseCareComponent.module.scss";

type ChronicObstructivePulmonaryDiseaseCareElements = IntegerReviewedCareElement | DecimalReviewedCareElement | BooleanReviewedCareElement | DateOnlyReviewedCareElement | TextReviewedCareElement | MultiSelectReviewedCareElement;

interface ChronicObstructivePulmonaryDiseaseBasicCareComponent extends CareComponent {
    CareElements: Array<ChronicObstructivePulmonaryDiseaseCareElements>;
}

const YES_NO_NAMES: ReadonlySet<string> = new Set<string>([
    FlowsheetConstants.ChronicObstructivePulmonaryDiseaseBasic.AssessmentAndMonitoring.SpirometryTest,
    FlowsheetConstants.ChronicObstructivePulmonaryDiseaseBasic.AssessmentAndMonitoring.NeedForSupplementalOxygen,
    FlowsheetConstants.ChronicObstructivePulmonaryDiseaseBasic.AssessmentAndMonitoring.NeedForNocturnalVentilation
]);

const isTopicSubSection = (s: string): boolean =>
    s === FlowsheetConstants.ChronicObstructivePulmonaryDiseaseBasic.PatientEducation.SubSectionName ||
  s === FlowsheetConstants.ChronicObstructivePulmonaryDiseaseBasic.TreatmentAndManagement.SubSectionName;

function isYesNoName(name: string): boolean {
    return YES_NO_NAMES.has(name);
}

function buildCopdOptions(getText: (k: string) => string): InlineOption<string>[] {
    const values = Object.values(ChronicObstructivePulmonaryDiseaseClassifications);
    return values.map(name =>
        new InlineOption<string>(
            name,
            getText(`Flowsheets_ChronicObstructivePulmonaryDiseaseBasicCareComponentOption_${name}`)
        ));
}

const LabelKeyByOption: Record<ArterialBloodGasOptions, string> = {
    [ArterialBloodGasOptions.Yes]: "YesLabel",
    [ArterialBloodGasOptions.No]: "NoLabel",
    [ArterialBloodGasOptions.NotAvailable]: "NALabel"
};

function buildArterialBloodgasOptions(getText: (k: string) => string): InlineOption<string>[] {
    const values: ArterialBloodGasOptions[] =
    Object.values(ArterialBloodGasOptions) as ArterialBloodGasOptions[];

    return values.map((name: ArterialBloodGasOptions): InlineOption<string> =>
        new InlineOption<string>(name, getText(LabelKeyByOption[name]))
    );
}

const ChronicObstructivePulmonaryDiseaseBasicCareComponent = React.memo(
    ({ isEditable, state, mutateState }: ICareComponentProps<ChronicObstructivePulmonaryDiseaseBasicCareComponent>): JSX.Element => {
        const { getText } = useTranslation();
        const coreCareElement = state?.CareElements?.toLookup(p => p.SubSectionName) ?? {};
        const keys = Object.keys(coreCareElement);

        function replaceCareElement(updated: ChronicObstructivePulmonaryDiseaseCareElements): void {
            mutateState(state => {
                state.CareElements = [...state.CareElements];
                const index = state.CareElements.findIndex(p => p.Name === updated.Name);
                if (index !== -1) state.CareElements[index] = updated;
            });
        }

        function renderBooleanReviewed(
            careElement: BooleanReviewedCareElement,
            name: string,
            isEditable: boolean
        ): JSX.Element {
            if (isYesNoName(careElement.Name)) {
                return (
                    <CareElementYesNoControl
                        key={`${careElement.Name}-${state.Id}`}
                        label={getText(`Flowsheets_ChronicObstructivePulmonaryDiseaseBasicCareComponentLabel_${name}`)}
                        isEditable={isEditable}
                        element={careElement}
                        onChange={element => replaceCareElement(element)}
                    />
                );
            }

            return (
                <CareElementDateReviewedCheckbox
                    key={`${careElement.Name}-${state.Id}`}
                    label={getText(`Flowsheets_ChronicObstructivePulmonaryDiseaseBasicCareComponentLabel_${name}`)}
                    isEditable={isEditable}
                    element={careElement}
                    onChange={element => replaceCareElement(element)}
                />
            );
        }

        if (!coreCareElement)
            return null;

        return (
            <div>
                {keys.map(key => {
                    const careElementsBySection = coreCareElement[key];
                    if (!careElementsBySection || careElementsBySection.length === 0)
                        return null;

                    return (
                        <ReviewedCareElementContainer key={`${FlowsheetConstants.ChronicObstructivePulmonaryDiseaseBasic.CareComponentName}_${key}`}>
                            <div className={`${styles.subsectionTitleLabel} ${styles.spanRight}`}>{getText(`Flowsheets_ChronicObstructivePulmonaryDiseaseBasicCareComponent_SubSectionName_${key}`)}</div>
                            {isTopicSubSection(key) && (
                                <div className={`${styles.topicSubSectionTitle} ${styles.spanRight}`}>
                                    {getText("Flowsheets_TopicLabel")}
                                </div>
                            )}
                            {careElementsBySection.map((careElement => {
                                if (careElement.CareElementType === CareElementType.IntegerReviewed) {
                                    return (
                                        <CareElementDateReviewedNumber
                                            key={`${careElement.Name}-${state.Id}`}
                                            label={getText(`Flowsheets_ChronicObstructivePulmonaryDiseaseBasicCareComponentLabel_${careElement.Name}`)}
                                            isEditable={isEditable}
                                            element={careElement}
                                            onChange={element => replaceCareElement(element)}
                                            type="integer"
                                            side="row"
                                        />
                                    );
                                }
                                if (careElement.CareElementType === CareElementType.DateOnlyReviewed) {
                                    return (
                                        <CareElementDateOnlyDateReviewed
                                            key={`${careElement.Name}-${state.Id}`}
                                            label={getText(`Flowsheets_ChronicObstructivePulmonaryDiseaseBasicCareComponentLabel_${careElement.Name}`)}
                                            isEditable={isEditable}
                                            element={careElement}
                                            onChange={element => replaceCareElement(element)}
                                        />
                                    );
                                }
                                if (careElement.CareElementType === CareElementType.TextReviewed && careElement.Name === FlowsheetConstants.ChronicObstructivePulmonaryDiseaseBasic.AssessmentAndMonitoring.ChronicObstructivePulmonaryDiseaseClassification) {
                                    const options = buildCopdOptions(getText);

                                    return (
                                        <CareElementDateReviewedRadioOptionsControl
                                            key={`${careElement.Name}-${state.Id}`}
                                            label={getText(`Flowsheets_ChronicObstructivePulmonaryDiseaseBasicCareComponentLabel_${careElement.Name}`)}
                                            isEditable={isEditable}
                                            element={careElement}
                                            options={options}
                                            onChange={updated => {
                                                replaceCareElement(updated);
                                            }}
                                            side="column"
                                        />
                                    );
                                }
                                if (careElement.CareElementType === CareElementType.TextReviewed && careElement.Name === FlowsheetConstants.ChronicObstructivePulmonaryDiseaseBasic.DiagnosticsTests.ArterialBloodGasTest) {
                                    const options = buildArterialBloodgasOptions(getText);

                                    return (
                                        <CareElementDateReviewedRadioOptionsControl
                                            key={`${careElement.Name}-${state.Id}`}
                                            label={getText(`Flowsheets_ChronicObstructivePulmonaryDiseaseBasicCareComponentLabel_${careElement.Name}`)}
                                            isEditable={isEditable}
                                            element={careElement}
                                            options={options}
                                            onChange={updated => {
                                                replaceCareElement(updated);
                                            }}
                                            side="row"
                                        />
                                    );
                                }
                                if (careElement.CareElementType === CareElementType.MultiSelectReviewed) {
                                    return (
                                        <CareElementOptionsControl
                                            key={`${careElement.Name}-${state.Id}`}
                                            label={getText(`Flowsheets_ChronicObstructivePulmonaryDiseaseBasicCareComponentLabel_${careElement.Name}`)}
                                            isEditable={isEditable}
                                            element={careElement}
                                            onChange={element => mutateState(state => {
                                                state.CareElements = [...state.CareElements];
                                                const index = state.CareElements.findIndex(p => p.Name === element.Name);
                                                if (index !== -1) {
                                                    state.CareElements[index] = element;
                                                }
                                            })}
                                            side="column"
                                            options={[
                                                new InlineOption(FlowsheetConstants.ChronicObstructivePulmonaryDiseaseBasic.TreatmentAndManagement.ExacerbationPlanProvidedOptionName, getText("ProvidedLabel")),
                                                new InlineOption(FlowsheetConstants.ChronicObstructivePulmonaryDiseaseBasic.TreatmentAndManagement.ExacerbationPlanRevisedOptionName, getText("RevisedLabel")),
                                                new InlineOption(FlowsheetConstants.ChronicObstructivePulmonaryDiseaseBasic.TreatmentAndManagement.ExacerbationPlanReviewedOptionName, getText("ReviewedLabel"))
                                            ]}
                                        />
                                    );
                                }
                                if (careElement.CareElementType === CareElementType.BooleanReviewed) {
                                    return renderBooleanReviewed(
                                        careElement,
                                        careElement.Name,
                                        isEditable
                                    );
                                }
                                return null;
                            }))}
                        </ReviewedCareElementContainer>
                    );
                })}
                <CareComponentNotes
                    id={`ChronicObstructivePulmonaryDiseaseBasicCareComponentNotes-${state.Id}`}
                    dataCy="ChronicObstructivePulmonaryDiseaseBasicCareComponentNotes"
                    value={state.Note}
                    isEditable={isEditable}
                    onTextChange={newValue => mutateState(s => s.Note = newValue)}
                />
            </div>
        );
    }
);

export { ChronicObstructivePulmonaryDiseaseBasicCareComponent };
